import { useState, useEffect, useMemo } from "react";
import { TextInput, Row } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { currencyToCents } from "byzantine";
import Filters from "byzantine/src/filters";
import { FILTER_CONFIG, FilterChips, FilterType } from "./filterConfig";
import { ActionBar, FilterChip } from "./common";
import styles from "./Filters.module.scss";

const AmountFilter = ({
  filters,
  isActive,
  handleSetActive,
  handleSetFilter,
  handleToggleFilter,
}: FilterType) => {
  const { l10n } = useLocalization();
  const [amountRange, setAmountRange] = useState<{
    min: Cents | undefined;
    max: Cents | undefined;
  }>({
    min: undefined,
    max: undefined,
  });

  useEffect(() => {
    const { min, max } = FILTER_CONFIG[FilterChips.AMOUNT].queryKeys;
    setAmountRange({
      min:
        filters[min] !== undefined
          ? (Number(filters[min]) as Cents)
          : undefined,
      max:
        filters[max] !== undefined
          ? (Number(filters[max]) as Cents)
          : undefined,
    });
  }, [filters]);

  const handleToggleChip = () => {
    handleSetActive(isActive ? "" : FilterChips.AMOUNT);
  };

  const handleApplyFilter = () => {
    const { min, max } = FILTER_CONFIG[FilterChips.AMOUNT].queryKeys;
    handleSetFilter({
      ...filters,
      [min]: amountRange.min,
      [max]: amountRange.max,
    });
    handleToggleChip();
  };

  const handleRemoveFilter = () => {
    handleToggleFilter(FilterChips.AMOUNT);
  };

  // converts it to "currency" with dollar sign
  const getDisplayValue = (value: Cents | undefined) => {
    if (value === undefined) {
      return "";
    }
    return Filters.currency(value);
  };

  const defaultLabel = l10n.getString(
    FILTER_CONFIG[FilterChips.AMOUNT].labelKey
  );

  const label = useMemo(() => {
    const { min: minKey, max: maxKey } =
      FILTER_CONFIG[FilterChips.AMOUNT].queryKeys;
    const min =
      filters[minKey] !== undefined
        ? (Number(filters[minKey]) as Cents)
        : undefined;
    const max =
      filters[maxKey] !== undefined
        ? (Number(filters[maxKey]) as Cents)
        : undefined;

    // show user inputted 0
    const hasMin = min !== undefined;
    const hasMax = max !== undefined;

    const formattedMin = hasMin ? getDisplayValue(min) : undefined;
    const formattedMax = hasMax ? getDisplayValue(max) : undefined;

    if (hasMin && hasMax) {
      return `${formattedMin} - ${formattedMax}`;
    }

    if (hasMin) {
      return `> ${formattedMin}`;
    }

    if (hasMax) {
      return `< ${formattedMax}`;
    }

    return defaultLabel;
  }, [filters]);

  const hasError =
    typeof amountRange.min === "number" &&
    typeof amountRange.max === "number" &&
    amountRange.min > amountRange.max;

  const renderDropdown = () => (
    <>
      <div className={styles.filterDropdownContent}>
        <Row>
          <Row.Item>
            <div className={styles.filterInput}>
              <TextInput
                label={l10n.getString("label-min-amount")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAmountRange({
                    ...amountRange,
                    min: currencyToCents(e.target.value),
                  });
                }}
                value={getDisplayValue(amountRange.min)}
                error={
                  hasError ? l10n.getString("start-amount-error") : undefined
                }
              />
            </div>
          </Row.Item>
          <Row.Item>
            <div className={styles.filterInput}>
              <TextInput
                label={l10n.getString("label-max-amount")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAmountRange({
                    ...amountRange,
                    max: currencyToCents(e.target.value),
                  });
                }}
                value={getDisplayValue(amountRange.max)}
                error={
                  hasError ? l10n.getString("end-amount-error") : undefined
                }
              />
            </div>
          </Row.Item>
        </Row>
      </div>
      <ActionBar
        handleApplyFilter={handleApplyFilter}
        handleRemoveFilter={handleRemoveFilter}
        disableApplyFilter={hasError}
      />
    </>
  );

  return (
    <FilterChip
      dropdownContent={renderDropdown()}
      isActive={isActive}
      handleToggleChip={handleToggleChip}
      label={label}
    />
  );
};

export default AmountFilter;
